/**
 * Progression par XP : coût et application des achats (V5).
 * Fonctions pures, testées ; l'interface est dans components/VtmXpModal.
 */
import {ATTRIBUTES_BY_ID, SKILLS_BY_ID, localized} from './data/traits';
import {DISCIPLINES_BY_ID, POWERS_BY_ID} from './data/disciplines';
import {CLANS_BY_ID} from './data/clans';
import {ADVANTAGE_KIND, MAX_ADVANTAGE_LEVEL, createAdvantage, respectsPowerTiers} from './character';

export const PURCHASE_TYPES = {
  ATTRIBUTE: 'attribute',
  SKILL: 'skill',
  SPECIALTY: 'specialty',
  DISCIPLINE: 'discipline',
  NEW_ADVANTAGE: 'newAdvantage',
  ADVANTAGE_DOT: 'advantageDot',
  BLOOD_POTENCY: 'bloodPotency',
};

export const MAX_TRAIT = 5;
export const MAX_BLOOD_POTENCY = 10;

// Coûts V5 : multiplicateur × nouveau niveau, ou prix fixe
export const XP_COSTS = {
  attribute: 5,
  skill: 3,
  specialty: 3,
  clanDiscipline: 5,
  caitiffDiscipline: 6,
  otherDiscipline: 7,
  advantageDot: 3,
  bloodPotency: 10,
};

export const getXpAvailable = (sheet) => (sheet.xp?.total || 0) - (sheet.xp?.spent || 0);

/** Multiplicateur d'une discipline selon le clan : de clan × 5, Caïtiff × 6, hors clan × 7. */
export const getDisciplineMultiplier = (clanId, disciplineId) => {
  const clan = CLANS_BY_ID[clanId];
  if (clan?.anyDisciplines) return XP_COSTS.caitiffDiscipline;
  if (clan?.disciplines.includes(disciplineId)) return XP_COSTS.clanDiscipline;
  return XP_COSTS.otherDiscipline;
};

/** Pouvoirs qu'un nouveau point de discipline permet de prendre (niveau, paliers, amalgames, pas déjà possédé). */
export const getPowersForNextDot = (sheet, disciplineId) => {
  const current = sheet.disciplines[disciplineId] || {level: 0, powers: []};
  const nextLevel = current.level + 1;
  const owned = current.powers || [];
  const ownedLevels = owned.map(id => POWERS_BY_ID[id]?.level || 0);
  return Object.values(POWERS_BY_ID).filter(power => (
    power.discipline === disciplineId
    && power.level <= nextLevel
    && !owned.includes(power.id)
    && (!power.amalgam || (sheet.disciplines[power.amalgam.discipline]?.level || 0) >= power.amalgam.level)
    && respectsPowerTiers([...ownedLevels, power.level], nextLevel)
  ));
};

const error = (reason) => ({ok: false, reason});

/**
 * Prépare un achat : vérifie qu'il est possible et calcule la fiche obtenue.
 * @returns {{ok: true, cost: Number, sheet: Object, summary: {fr: String, en: String}} | {ok: false, reason: String}}
 */
export const preparePurchase = (sheet, purchase) => {
  let result;
  switch (purchase.type) {
    case PURCHASE_TYPES.ATTRIBUTE: {
      const current = sheet.attributes[purchase.id];
      if (!ATTRIBUTES_BY_ID[purchase.id]) return error('unknown');
      if (current >= MAX_TRAIT) return error('max');
      const name = ATTRIBUTES_BY_ID[purchase.id].name;
      result = {
        cost: (current + 1) * XP_COSTS.attribute,
        sheet: {...sheet, attributes: {...sheet.attributes, [purchase.id]: current + 1}},
        summary: {fr: `${name.fr} ${current} → ${current + 1}`, en: `${name.en} ${current} → ${current + 1}`},
      };
      break;
    }
    case PURCHASE_TYPES.SKILL: {
      const current = sheet.skills[purchase.id] || 0;
      if (!SKILLS_BY_ID[purchase.id]) return error('unknown');
      if (current >= MAX_TRAIT) return error('max');
      const name = SKILLS_BY_ID[purchase.id].name;
      result = {
        cost: (current + 1) * XP_COSTS.skill,
        sheet: {...sheet, skills: {...sheet.skills, [purchase.id]: current + 1}},
        summary: {fr: `${name.fr} ${current} → ${current + 1}`, en: `${name.en} ${current} → ${current + 1}`},
      };
      break;
    }
    case PURCHASE_TYPES.SPECIALTY: {
      if (!SKILLS_BY_ID[purchase.skill]) return error('unknown');
      if (!(sheet.skills[purchase.skill] > 0)) return error('skillWithoutDots');
      const name = (purchase.name || '').trim();
      if (!name) return error('emptyName');
      if (sheet.specialties.some(item => item.skill === purchase.skill && item.name.toLowerCase() === name.toLowerCase())) {
        return error('duplicate');
      }
      const skill = SKILLS_BY_ID[purchase.skill].name;
      result = {
        cost: XP_COSTS.specialty,
        sheet: {...sheet, specialties: [...sheet.specialties, {skill: purchase.skill, name, source: 'xp'}]},
        summary: {fr: `Spécialité ${skill.fr} (${name})`, en: `Specialty ${skill.en} (${name})`},
      };
      break;
    }
    case PURCHASE_TYPES.DISCIPLINE: {
      const discipline = DISCIPLINES_BY_ID[purchase.id];
      if (!discipline || purchase.id === 'thinBloodAlchemy') return error('unknown');
      const current = sheet.disciplines[purchase.id] || {level: 0, powers: []};
      if (current.level >= MAX_TRAIT) return error('max');
      if (!getPowersForNextDot(sheet, purchase.id).some(power => power.id === purchase.powerId)) return error('invalidPower');
      const power = POWERS_BY_ID[purchase.powerId];
      result = {
        cost: (current.level + 1) * getDisciplineMultiplier(sheet.clan, purchase.id),
        sheet: {
          ...sheet,
          disciplines: {
            ...sheet.disciplines,
            [purchase.id]: {level: current.level + 1, powers: [...(current.powers || []), purchase.powerId]},
          },
        },
        summary: {
          fr: `${discipline.name.fr} ${current.level} → ${current.level + 1} (${power.name.fr})`,
          en: `${discipline.name.en} ${current.level} → ${current.level + 1} (${power.name.en})`,
        },
      };
      break;
    }
    case PURCHASE_TYPES.NEW_ADVANTAGE: {
      const name = (purchase.name || '').trim();
      const level = purchase.level || 1;
      if (!name) return error('emptyName');
      if (level < 1 || level > MAX_ADVANTAGE_LEVEL) return error('level');
      result = {
        cost: level * XP_COSTS.advantageDot,
        sheet: {
          ...sheet,
          advantages: [...sheet.advantages, createAdvantage({name, description: purchase.description || '', level, source: 'xp'})],
        },
        summary: {fr: `Avantage ${name} ${'●'.repeat(level)}`, en: `Advantage ${name} ${'●'.repeat(level)}`},
      };
      break;
    }
    case PURCHASE_TYPES.ADVANTAGE_DOT: {
      const item = sheet.advantages[purchase.index];
      if (!item || item.kind !== ADVANTAGE_KIND.ADVANTAGE) return error('unknown');
      if (item.level >= MAX_ADVANTAGE_LEVEL) return error('max');
      result = {
        cost: XP_COSTS.advantageDot,
        sheet: {
          ...sheet,
          advantages: sheet.advantages.map((advantage, index) => (index === purchase.index ? {...advantage, level: advantage.level + 1} : advantage)),
        },
        summary: {fr: `${item.name} ${item.level} → ${item.level + 1}`, en: `${item.name} ${item.level} → ${item.level + 1}`},
      };
      break;
    }
    case PURCHASE_TYPES.BLOOD_POTENCY: {
      const current = sheet.bloodPotency || 0;
      if (current >= MAX_BLOOD_POTENCY) return error('max');
      result = {
        cost: (current + 1) * XP_COSTS.bloodPotency,
        sheet: {...sheet, bloodPotency: current + 1},
        summary: {fr: `Puissance du Sang ${current} → ${current + 1}`, en: `Blood Potency ${current} → ${current + 1}`},
      };
      break;
    }
    default:
      return error('unknown');
  }
  if (result.cost > getXpAvailable(sheet)) return {...error('notEnoughXp'), cost: result.cost};
  return {
    ok: true,
    ...result,
    sheet: {...result.sheet, xp: {total: sheet.xp?.total || 0, spent: (sheet.xp?.spent || 0) + result.cost}},
  };
};

/** Champs de la fiche modifiés par un achat (pour une mise à jour Firestore ciblée). */
export const changedFields = (before, after) => Object.keys(after).filter(key => after[key] !== before[key])
  .reduce((acc, key) => ({...acc, [key]: after[key]}), {});

export const purchaseSummary = (prepared, lng = 'fr') => localized(prepared.summary, lng);
